/**
 * This module is responsible for all state and actions related to the current authenticated user.
 */
import { useToast } from 'vue-toastification'
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getSupabaseSession, supabase } from '../../helpers/init-db';
import { buildEdgeFunctionHeaders, buildEdgeFunctionUrl } from '../../helpers/edge-functions';
import { normalizeLoginRedirect } from '../../helpers/login-redirect';

dayjs.extend(relativeTime);

const toast = useToast();
let authStateSubscription = null;
const provisionAccountRequests = new Map();
const PROVISIONING_RETRY_DELAY_MS = 250;
const PROVISIONING_RETRY_ATTEMPTS = 5;

function isSuperUserProfile(profile) {
  return profile?.is_super_user === true;
}

function buildRole(profile) {
  if (isSuperUserProfile(profile)) {
    return 'gold_yearly';
  }

  const status = profile?.subscription_status ?? 'free';
  const plan = profile?.subscription_plan ?? 'free';
  const interval = profile?.subscription_interval ?? null;

  if (status === 'trialing') {
    return 'trial';
  }

  if (plan === 'free') {
    return 'free';
  }

  if (interval === 'month') {
    return `${plan}_monthly`;
  }

  if (interval === 'year') {
    return `${plan}_yearly`;
  }

  return plan;
}

function buildUserMetadata(profile, authUser) {
  const authMetadata = authUser?.user_metadata ?? {};
  const profileData = profile?.profile_data ?? {};

  return {
    ...authMetadata,
    ...profileData,
    avatar_url: profile?.avatar_url ?? profileData.avatar_url ?? authMetadata.avatar_url ?? '',
    full_name: profile?.full_name ?? profileData.full_name ?? authMetadata.full_name ?? authMetadata.name ?? '',
    timezone: profile?.timezone ?? profileData.timezone ?? authMetadata.timezone ?? 'UTC',
    performance: profileData.performance ?? authMetadata.performance ?? 'high',
    login_redirect: normalizeLoginRedirect(profileData.login_redirect ?? authMetadata.login_redirect),
  };
}

function buildAppMetadata(profile, authUser, userMetadata) {
  const authMetadata = authUser?.app_metadata ?? {};
  const accessEndsAt = profile?.access_expires_at ?? profile?.subscription_period_ends_at ?? null;
  const trialStart = userMetadata?.trial_start ?? (authUser?.created_at ? dayjs(authUser.created_at).unix() : null);
  const isSuperUser = isSuperUserProfile(profile);

  return {
    ...authMetadata,
    roles: [buildRole(profile)],
    status: isSuperUser ? 'active' : profile?.subscription_status,
    trial_end: profile?.trial_ends_at ? dayjs(profile.trial_ends_at).unix() : null,
    trial_start: trialStart,
    end_at: accessEndsAt ? dayjs(accessEndsAt).unix() : null,
    ek_token: userMetadata?.ek_token ?? authMetadata.ek_token ?? null,
    is_super_user: isSuperUser,
  };
}

function enrichCurrentUser(value) {
  if (!value) {
    return null;
  }

  const nextUser = {
    ...value,
    app_metadata: {
      ...(value.app_metadata ?? {}),
      roles: Array.isArray(value.app_metadata?.roles) ? value.app_metadata.roles : [],
    },
    user_metadata: {
      ...(value.user_metadata ?? {}),
    },
  };

  if (nextUser.app_metadata.roles.length) {
    const plan = String(nextUser.app_metadata.roles[0]).split('_');
    nextUser.app_plan = plan[0];
    nextUser.app_plan_duration = plan[1] ?? null;
  } else {
    nextUser.app_plan = 'none';
    nextUser.app_plan_duration = null;
  }

  nextUser.lock = false;
  nextUser.status = nextUser.app_metadata.status;
  nextUser.trial_end = null;
  nextUser.cancel_at = null;

  if (nextUser.app_metadata.status === 'trialing') {
    if (nextUser.app_metadata.trial_end && dayjs().unix() >= nextUser.app_metadata.trial_end) {
      nextUser.status = 'trial_expired';
      nextUser.lock = true;
    } else if (nextUser.app_metadata.trial_end) {
      const trialEnd = dayjs.unix(nextUser.app_metadata.trial_end);
      nextUser.trial_end = 'Trial expires ' + dayjs().to(trialEnd);
    }
  } else if (nextUser.status === 'active' && nextUser.app_metadata.end_at) {
    const cancelAt = dayjs.unix(nextUser.app_metadata.end_at);
    nextUser.cancel_at = 'Your plan will be cancelled on ' + dayjs(cancelAt).format('DD MMM YYYY');
  } else if (nextUser.status === 'canceled' && nextUser.app_metadata.end_at) {
    const cancelAt = dayjs.unix(nextUser.app_metadata.end_at).add(21, 'days');
    nextUser.cancel_at = 'Your account will be deleted ' + dayjs().to(cancelAt);
    nextUser.lock = true;
  } else if (nextUser.status === 'active') {
    // Active state.
  } else if (typeof nextUser.status === 'undefined') {
    nextUser.status = 'custom';
    nextUser.lock = false;
  } else if (!nextUser.status) {
    nextUser.status = 'unknown';
    nextUser.lock = true;
  }

  return nextUser;
}

async function fetchProfile(userId) {
  if (!supabase || !userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}

function userNeedsProvisioning(authUser, profile = null) {
  if (!profile) {
    return true;
  }

  if (authUser?.user_metadata?.needs_provisioning === true) {
    return true;
  }

  const profileData = profile?.profile_data ?? {};

  if (profileData?.needs_provisioning === true) {
    return true;
  }

  const hasProvisioningMarker = Boolean(
    profileData?.signup_provisioned_at
    || profileData?.ek_token
    || profileData?.trial_start
    || profile?.billing_customer_id
    || profile?.trial_ends_at
    || (profile?.subscription_status && profile.subscription_status !== 'free')
    || profile?.is_super_user === true
  );

  if (!hasProvisioningMarker) {
    return true;
  }

  return false;
}

function usesGoogleAuth(currentUser) {
  const authSource = currentUser?.user_metadata?.auth_source
    || currentUser?.user_metadata?.authSource;
  if (typeof authSource === 'string' && authSource.toLowerCase() === 'google') {
    return true;
  }

  const provider = currentUser?.raw_user?.app_metadata?.provider;
  if (typeof provider === 'string' && provider.toLowerCase() === 'google') {
    return true;
  }

  const providers = currentUser?.raw_user?.app_metadata?.providers;
  if (Array.isArray(providers)) {
    return providers.some(value => typeof value === 'string' && value.toLowerCase() === 'google');
  }

  return false;
}

async function provisionAccount(session) {
  const activeSession = await getSupabaseSession();
  const accessToken = activeSession?.access_token ?? session?.access_token;
  const provisionKey = session?.user?.id ?? accessToken;

  if (!accessToken) {
    return null;
  }

  if (provisionKey && provisionAccountRequests.has(provisionKey)) {
    return await provisionAccountRequests.get(provisionKey);
  }

  const requestPromise = (async () => {
    const response = await fetch(buildEdgeFunctionUrl('provision-account'), {
      method: 'POST',
      headers: buildEdgeFunctionHeaders({ accessToken }),
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.error || 'Account provisioning failed');
    }

    return data;
  })();

  if (provisionKey) {
    provisionAccountRequests.set(provisionKey, requestPromise);
  }

  try {
    return await requestPromise;
  } finally {
    if (provisionKey) {
      provisionAccountRequests.delete(provisionKey);
    }
  }
}

function normalizeHydrateSessionPayload(payload) {
  if (payload && payload.session) {
    return {
      session: payload.session,
      strictProvisioning: payload.strictProvisioning === true,
    };
  }

  return {
    session: payload,
    strictProvisioning: false,
  };
}

function shouldStrictlyHydrateSession(session) {
  return session?.user?.user_metadata?.needs_provisioning === true;
}

function mergeProvisionedSession(session, provisioned) {
  if (!provisioned?.user_metadata) {
    return session;
  }

  return {
    ...session,
    user: {
      ...session.user,
      user_metadata: {
        ...(session.user?.user_metadata ?? {}),
        ...(provisioned.user_metadata ?? {}),
      },
    },
  };
}

function sleep(ms) {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

async function waitForProvisionedProfile(userId) {
  let profile = null;

  for (let attempt = 0; attempt <= PROVISIONING_RETRY_ATTEMPTS; attempt += 1) {
    profile = await fetchProfile(userId);

    if (profile && profile?.profile_data?.needs_provisioning !== true) {
      return profile;
    }

    if (attempt < PROVISIONING_RETRY_ATTEMPTS) {
      await sleep(PROVISIONING_RETRY_DELAY_MS);
    }
  }

  return profile;
}

function buildCurrentUser(authUser, session, profile, currentUser = null) {
  const userMetadata = buildUserMetadata(profile, authUser);
  const appMetadata = buildAppMetadata(profile, authUser, userMetadata);

  return enrichCurrentUser({
    ...currentUser,
    avatar_url: profile?.avatar_url ?? userMetadata?.avatar_url ?? null,
    id: authUser.id,
    email: authUser.email,
    created_at: authUser.created_at,
    profile,
    raw_user: authUser,
    session,
    token: session
      ? {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at ? session.expires_at * 1000 : null,
          expires_in: session.expires_in,
          token_type: session.token_type,
        }
      : null,
    app_metadata: appMetadata,
    user_metadata: userMetadata,
  });
}

export default {
  strict: false,
  namespaced: true,

  state() {
    return {
      currentUser: null,
      authToken: false,
    };
  },
  getters: {
    loggedIn: state => !!state.currentUser,
    currentUser: state => state.currentUser,
    authToken: state => state.authToken,
  },
  mutations: {
    SET_TOKEN(state, value) {
      state.authToken = value;
    },
    SET_CURRENT_USER(state, value) {
      state.currentUser = enrichCurrentUser(value);
    },
  },
  actions: {
    async hydrateSession({ commit, state }, payload) {
      const { session, strictProvisioning } = normalizeHydrateSessionPayload(payload);

      if (!session?.user) {
        commit('SET_CURRENT_USER', null);
        return null;
      }

      try {
        let nextSession = session;
        let profile = await fetchProfile(session.user.id);

        if (userNeedsProvisioning(session.user, profile)) {
          try {
            const provisioned = await provisionAccount(session);
            nextSession = mergeProvisionedSession(session, provisioned);
            profile = provisioned?.profile ?? await waitForProvisionedProfile(session.user.id);

            if (strictProvisioning && (!profile || userNeedsProvisioning(nextSession.user, profile))) {
              throw new Error('Account provisioning is still pending');
            }
          } catch (error) {
            console.error('Account provisioning during session hydration failed:', error);

            if (strictProvisioning) {
              throw error;
            }
          }
        }

        if (strictProvisioning && !profile) {
          throw new Error('User profile is not ready yet');
        }

        const currentUser = buildCurrentUser(nextSession.user, nextSession, profile, state.currentUser);
        commit('SET_CURRENT_USER', currentUser);
        return currentUser;
      } catch (error) {
        console.error('Failed to hydrate current user from Supabase:', error);

        if (strictProvisioning) {
          throw error;
        }

        const fallbackUser = buildCurrentUser(session.user, session, null, state.currentUser);
        commit('SET_CURRENT_USER', fallbackUser);
        return fallbackUser;
      }
    },

    async attemptLogin({ dispatch }, credentials) {
      if (!supabase) {
        throw new Error('Supabase is not configured');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        throw error;
      }

      const currentUser = await dispatch('hydrateSession', {
        session: data.session,
        strictProvisioning: shouldStrictlyHydrateSession(data.session),
      });
      toast.success('Logged in.');
      return currentUser;
    },

    async attemptExternalLogin(_store, providerInput) {
      if (!supabase) {
        throw new Error('Supabase is not configured');
      }

      const provider = typeof providerInput === 'string'
        ? providerInput
        : providerInput?.provider;
      const redirectTo = typeof providerInput === 'string'
        ? window.location.origin
        : providerInput?.redirectTo || window.location.origin;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: String(provider).toLowerCase(),
        options: {
          redirectTo,
        },
      });

      if (error) {
        throw error;
      }
    },

    async completeExternalLogin({ dispatch }) {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      if (!data.session) {
        throw new Error('No active authentication session found');
      }

      await dispatch('hydrateSession', {
        session: data.session,
        strictProvisioning: shouldStrictlyHydrateSession(data.session),
      });
      return 'Signed in successfully';
    },

    async attemptSignup({ dispatch }, credentials) {
      if (!supabase) {
        throw new Error('Supabase is not configured');
      }

      const redirectTo = new URL('/confirm-email', window.location.origin).toString();
      const { data, error } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          emailRedirectTo: redirectTo,
          data: {
            full_name: credentials.name,
            name: credentials.name,
            needs_provisioning: true,
          },
        },
      });

      if (error) {
        throw error;
      }

      let currentUser = null;

      if (data.session?.access_token) {
        const provisioned = await provisionAccount(data.session);
        currentUser = await dispatch('hydrateSession', {
          session: mergeProvisionedSession(data.session, provisioned),
          strictProvisioning: true,
        });
      }

      return {
        ...data,
        currentUser,
        requiresEmailConfirmation: !data.session,
      };
    },

    async attemptConfirmation({ commit, dispatch }, token) {
      commit('SET_TOKEN', true);

      try {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'signup',
        });

        if (error) {
          throw error;
        }

        if (data.session?.access_token) {
          const provisioned = await provisionAccount(data.session);
          const currentUser = await dispatch('hydrateSession', {
            session: mergeProvisionedSession(data.session, provisioned),
            strictProvisioning: true,
          });

          return {
            email: data.user?.email ?? '',
            currentUser,
          };
        }

        commit('SET_CURRENT_USER', null);
        return { email: data.user?.email ?? '', currentUser: null };
      } finally {
        commit('SET_TOKEN', false);
      }
    },

    async attemptLogout({ commit }) {
      if (!supabase) {
        commit('SET_CURRENT_USER', null);
        return null;
      }

      const { error } = await supabase.auth.signOut();
      commit('SET_CURRENT_USER', null);

      if (error) {
        throw error;
      }

      return true;
    },

    getUserJWTToken({ getters }) {
      if (!getters.currentUser?.token?.access_token) {
        alert('Please sign in again');
        console.warn('User needs to sign in again');
        return;
      }

      alert(`got user token: ${getters.currentUser.token.access_token}`);
    },

    async attemptRefresh({ dispatch, commit }) {
      if (!supabase) {
        commit('SET_CURRENT_USER', null);
        return null;
      }

      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error('Failed to refresh user token:', error);
        commit('SET_CURRENT_USER', null);
        throw error;
      }

      if (!data.session) {
        commit('SET_CURRENT_USER', null);
        return null;
      }

      return await dispatch('hydrateSession', {
        session: data.session,
        strictProvisioning: shouldStrictlyHydrateSession(data.session),
      });
    },

    async fetchUserProfileFromSupabase({ state, dispatch }) {
      if (!state.currentUser?.session) {
        return null;
      }

      return await dispatch('hydrateSession', {
        session: state.currentUser.session,
        strictProvisioning: shouldStrictlyHydrateSession(state.currentUser.session),
      });
    },

    getCurrentUser({ state }) {
      console.log('User Object', state.currentUser);
    },

    async initAuth({ commit, dispatch }) {
      if (!supabase) {
        commit('SET_CURRENT_USER', null);
        return;
      }

      if (!authStateSubscription) {
        const { data } = supabase.auth.onAuthStateChange((_event, session) => {
          dispatch('hydrateSession', {
            session,
            strictProvisioning: shouldStrictlyHydrateSession(session),
          });
        });
        authStateSubscription = data.subscription;
      }

      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Failed to load auth session:', error);
        commit('SET_CURRENT_USER', null);
        return;
      }

      await dispatch('hydrateSession', {
        session: data.session,
        strictProvisioning: shouldStrictlyHydrateSession(data.session),
      });
    },

    async requestPasswordRecover(_store, email) {
      const redirectTo = new URL('/recover', window.location.origin).toString();
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

      if (error) {
        throw error;
      }

      return true;
    },

    async attemptPasswordRecovery({ dispatch }, token) {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: 'recovery',
      });

      if (error) {
        console.error('Failed to verify recover token:', error);
        throw error;
      }

      return await dispatch('hydrateSession', {
        session: data.session,
        strictProvisioning: shouldStrictlyHydrateSession(data.session),
      });
    },

    async updateUserAccount({ state, dispatch }, userData) {
      if (!supabase || !state.currentUser?.id) {
        throw new Error('User not authenticated');
      }

      const currentProfile = state.currentUser.profile ?? await fetchProfile(state.currentUser.id);
      const currentProfileData = currentProfile?.profile_data ?? {};
      const currentMetadata = state.currentUser.user_metadata ?? {};
      const nextData = {
        ...currentMetadata,
        ...currentProfileData,
        ...(userData?.data ?? {}),
      };

      const fullName = typeof nextData.full_name === 'string'
        ? nextData.full_name
        : (currentProfile?.full_name ?? state.currentUser.user_metadata?.full_name ?? null);
      const timezone = typeof nextData.timezone === 'string'
        ? nextData.timezone
        : (currentProfile?.timezone ?? 'UTC');
      const avatarUrl = typeof userData?.avatar_url === 'string'
        ? userData.avatar_url
        : (currentProfile?.avatar_url ?? state.currentUser.avatar_url ?? null);

      const authUpdate = {
        data: {
          ...state.currentUser.raw_user?.user_metadata,
          ...nextData,
          avatar_url: avatarUrl,
          full_name: fullName,
          name: fullName,
          timezone,
        },
      };

      if (userData?.email && userData.email !== state.currentUser.email) {
        authUpdate.email = userData.email;
      }

      const { error: authError } = await supabase.auth.updateUser(authUpdate);
      if (authError) {
        throw authError;
      }

      const profileUpdate = {
        avatar_url: avatarUrl,
        full_name: fullName,
        timezone,
        profile_data: nextData,
      };

      if (nextData.onboarding === true && !currentProfile?.onboarding_completed_at) {
        profileUpdate.onboarding_completed_at = new Date().toISOString();
      }

      const { error: profileError } = await supabase
        .from('users')
        .update(profileUpdate)
        .eq('id', state.currentUser.id);

      if (profileError) {
        throw profileError;
      }

      const { data, error } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }

      return await dispatch('hydrateSession', {
        session: data.session,
        strictProvisioning: shouldStrictlyHydrateSession(data.session),
      });
    },

    async updateUserPassword({ state }, userData) {
      if (!supabase || !state.currentUser?.email) {
        throw new Error('User not authenticated');
      }

      if (usesGoogleAuth(state.currentUser)) {
        throw new Error('Password changes are not available for Google-authenticated accounts');
      }

      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: state.currentUser.email,
        password: userData.currentPassword,
      });

      if (verifyError) {
        throw verifyError;
      }

      const { data, error } = await supabase.auth.updateUser({
        password: userData.password,
      });

      if (error) {
        throw error;
      }

      return data;
    },

    async requestImpersonationLink({ state }, email) {
      if (!state.currentUser) {
        throw new Error('User not authenticated');
      }

      if (state.currentUser?.app_metadata?.is_super_user !== true) {
        throw new Error('Only super users can impersonate other accounts');
      }

      const normalizedEmail = String(email || '').trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error('A target email is required');
      }

      const session = await getSupabaseSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error('Authentication error. Please sign in again.');
      }

      const response = await fetch(buildEdgeFunctionUrl('impersonate-user'), {
        method: 'POST',
        headers: buildEdgeFunctionHeaders({
          accessToken,
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        body: JSON.stringify({ email: normalizedEmail }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Unable to start impersonation');
      }

      const actionLink = data?.action_link;
      if (typeof actionLink !== 'string' || !actionLink) {
        throw new Error('Unable to start impersonation');
      }

      return actionLink;
    },
  },
};